import { GrantedWebSettingsQuery, GrantedWebSettingsQueryVariables } from 'gql'
import { GetStaticPaths, GetStaticProps } from 'next'
import { Fragment, useEffect } from 'react'

import sections from 'components/sections'
import MetaTags from 'components/shared/MetaTags'
import HomePageLayout from 'components/ui/Layouts/HomePageLayout'
import { useDataLayer } from '~hooks'
import { addApolloState, initializeApollo } from '~services/apollo'
import { getServerTimeInApiFormat } from '~utils/graphql'
import EditSectionWrapper from 'components/home/block/edit-section-wrapper'
import { GRANTED_WEB_SETTINGS } from 'gql/platform/queries'
import { PARTNER_SLUG } from 'constants/constant'

export const getStaticPaths: GetStaticPaths = async () => {
  const apollo = initializeApollo()

  const { data } = await apollo.query<GrantedWebSettingsQuery, GrantedWebSettingsQueryVariables>({
    query: GRANTED_WEB_SETTINGS,
    variables: {
      type: 'marketing-page',
      partnerSlug: PARTNER_SLUG,
    },
  })

  const paths = data.grantedWebSettings.edges.map(({ node: { name } }) => ({
    params: {
      slug: name,
    },
  }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = async ({ params: { slug } }) => {
  const apollo = initializeApollo()

  const announceDate = getServerTimeInApiFormat(true)

  const { data } = await apollo.query<GrantedWebSettingsQuery, GrantedWebSettingsQueryVariables>({
    query: GRANTED_WEB_SETTINGS,
    variables: {
      name: Array.isArray(slug) ? slug[0] : slug,
      type: 'marketing-page',
      partnerSlug: PARTNER_SLUG,
    },
  })

  if (!data.grantedWebSettings.edges.length) {
    return {
      notFound: true,
    }
  }

  const { node } = data.grantedWebSettings.edges[0]

  const marketingPageData = {
    ...node,
    settings: JSON.parse(node.settings),
    sections: node.sections
      .filter((section) => section.isVisible)
      .map((section) => ({
        ...section,
        settings: JSON.parse(section.settings),
      })),
  }

  return addApolloState(apollo, {
    props: {
      isPrivate: marketingPageData.settings?.isPrivate,
      marketingPageData,
      announceDate,
    },
    revalidate: 10,
  })
}

const MarketingPage = ({ marketingPageData, announceDate }) => {
  const dl = useDataLayer()

  useEffect(() => {
    dl.push({ event: 'event_detail_page_view' })
  }, [])

  const formattedSectionsData = marketingPageData.sections
    .map((section) => section)
    .sort((a, b) => {
      const { sections: sectionsOrder } = marketingPageData.settings
      return sectionsOrder.indexOf(Number(a.pk)) - sectionsOrder.indexOf(Number(b.pk))
    })

  return (
    <>
      <MetaTags title={marketingPageData.settings?.pageTitle} />
      <HomePageLayout>
        {formattedSectionsData.map(({ type, settings, name }, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Fragment key={`${name}-${index}}`}>
            <EditSectionWrapper name={name}>
              {sections[type]?.({ ...settings, announceDate })}
            </EditSectionWrapper>
          </Fragment>
        ))}
      </HomePageLayout>
    </>
  )
}

export default MarketingPage
